/**
 * **Los gastos del mes y las obligaciones.**
 *
 * El importe de un gasto no se corrige: se **registra otro** que rige desde una fecha, y por eso
 * tiene ruta propia —`useSetExpenseAmount`— en vez de ir dentro del `PATCH`. Está aparte de la
 * nómina aunque las dos pantallas se editen igual, porque lo que cada una refresca no es lo mismo.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/lib/api";
import { getUndoGeneration, pick, recordChange } from "@/lib/undo";

import { keys } from "./claves";
import { cached, changedKeys, useUndoable } from "./deshacer";
import type { ClassificationChanges } from "./estructura";

import type { Behavior, CostElementCode, Expense, Obligation, OverheadSummary, Shares } from "../types";

// ── Gastos ────────────────────────────────────────────────
export const useExpenses = () => useQuery({ queryKey: keys.expenses, queryFn: () => apiGet<Expense[]>("/overhead/expenses") });
export const useObligations = () =>
  useQuery({ queryKey: keys.obligations, queryFn: () => apiGet<Obligation[]>("/overhead/obligations") });
export const useOverheadSummary = () =>
  useQuery({ queryKey: keys.overhead, queryFn: () => apiGet<OverheadSummary>("/overhead/summary") });

function useRefreshOverhead() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["overhead"] }),
      client.invalidateQueries({ queryKey: keys.structure }),
      // Incluye todos los formatos y simulaciones, aunque su caché todavía esté fresca.
      client.invalidateQueries({ queryKey: keys.baseCost().slice(0, 2) }),
      client.invalidateQueries({ queryKey: ["cost-base", "lisa"] }),
    ]);
}

type ExpenseChanges = Partial<{
  name: string;
  is_active: boolean;
  review_note: string | null;
}> &
  ClassificationChanges;

export function useUpdateExpense(id: string) {
  const client = useQueryClient();
  const patch = (body: ExpenseChanges) => apiPatch<Expense>(`/overhead/expenses/${id}`, body);
  return useUndoable(
    patch,
    (body) => {
      const expense = cached<Expense>(client, keys.expenses, id);
      return expense ? { label: `Cambio de ${expense.name}`, undo: () => patch(pick(expense, changedKeys(body))) } : null;
    },
    useRefreshOverhead(),
  );
}

/**
 * Cambiar el importe de un gasto **no es corregirlo: es registrar otro que rige desde hoy**.
 *
 * El `PATCH` dejó de aceptar `monthly_amount` (migración 0029) porque editarlo cambiaba en
 * silencio el costo de todos los meses ya cerrados. Y no lo rechaza: lo **ignora**, que es
 * peor — la pantalla decía «guardado» y el importe seguía igual. Ahora va por su propia ruta
 * y el gasto guarda desde cuándo rige cada valor.
 *
 * Deshacer no borra el nuevo importe: **registra otra vez el anterior**, igual que con los
 * precios de compra. La historia de lo que costó cada mes no se reescribe.
 */
export function useSetExpenseAmount(id: string) {
  const client = useQueryClient();
  const post = (body: { monthly_amount: number | null; valid_from?: string; note?: string }) =>
    apiPost<Expense>(`/overhead/expenses/${id}/amounts`, body);
  return useUndoable(
    post,
    () => {
      const expense = cached<Expense>(client, keys.expenses, id);
      return expense
        ? {
            label: `Importe de ${expense.name}`,
            undo: () => post({ monthly_amount: expense.monthly_amount }),
          }
        : null;
    },
    useRefreshOverhead(),
  );
}

/** Datos mínimos para dar de alta un gasto del mes. */
export type NewExpense = {
  name: string;
  monthly_amount?: number | null;
  cost_element: CostElementCode;
  cost_behavior: Behavior;
  variable_share?: number;
};

/** Agrega un gasto. Deshacer lo deja inactivo: la historia no se borra. */
export function useCreateExpense() {
  const refresh = useRefreshOverhead();
  return useMutation({
    mutationFn: async (body: NewExpense) => {
      const generation = getUndoGeneration();
      const expense = await apiPost<Expense>("/overhead/expenses", body);
      const setActive = (is_active: boolean) => apiPatch<Expense>(`/overhead/expenses/${expense.id}`, { is_active });
      recordChange({
        label: `Alta de ${expense.name}`,
        undo: () => setActive(false),
        redo: () => setActive(true),
      }, generation);
      return expense;
    },
    onSuccess: refresh,
  });
}

/** Borra un gasto y su reparto. Definitivo, igual que el de personas. */
export function useDeleteExpense(id: string) {
  const refresh = useRefreshOverhead();
  return useMutation({ mutationFn: () => apiDelete(`/overhead/expenses/${id}`), onSuccess: refresh });
}

export function useSetExpenseShares(id: string) {
  const client = useQueryClient();
  const put = (shares: Shares) => apiPut<Expense>(`/overhead/expenses/${id}/allocations`, { shares });
  return useUndoable(
    put,
    () => {
      const expense = cached<Expense>(client, keys.expenses, id);
      return expense ? { label: `Reparto de ${expense.name}`, undo: () => put(expense.allocations) } : null;
    },
    useRefreshOverhead(),
  );
}

type ObligationChanges = Partial<{ monthly_principal: number; monthly_interest: number; monthly_insurance: number }>;

export function useUpdateObligation(id: string) {
  const client = useQueryClient();
  const patch = (body: ObligationChanges) => apiPatch<Obligation>(`/overhead/obligations/${id}`, body);
  return useUndoable(
    patch,
    (body) => {
      const obligation = cached<Obligation>(client, keys.obligations, id);
      return obligation ? { label: `Cambio de ${obligation.name}`, undo: () => patch(pick(obligation, Object.keys(body))) } : null;
    },
    useRefreshOverhead(),
  );
}
